/**
 * The help: opened once by itself, on a first visit, and from the header's ?
 * any time after.
 *
 * Whether it has been shown lives in localStorage rather than the database. It
 * is about this browser having seen it, not about the ledger — restoring a
 * backup or erasing everything should not bring it back, and a new device
 * should show it.
 */
import { openSheet } from './state'

const KEY = 'okane.helpSeen'

/**
 * The steps, in order. Each one's words are `help.<key>.title`, `.lead` and
 * `.p1`…`.p<points>`, looked up by building the key — so a test walks this list
 * to prove every one of them exists in every catalogue.
 */
export const HELP_STEPS = [
  { key: 'transactions', icon: 'wallet', points: 3 },
  { key: 'inOut', icon: 'upload', points: 4 },
  { key: 'categories', icon: 'folder', points: 4 },
  { key: 'tags', icon: 'tag', points: 3 },
  // Shown as they are typed, so they are not words to translate.
  { key: 'batches', icon: 'pencil', points: 3, tokens: ['category:nothing', 'is:spending', 'min:100', 'tag:'] },
  { key: 'ready', icon: 'spark', points: 2 },
]

/** Every catalogue key the help will ask for. */
export const helpKeys = () =>
  HELP_STEPS.flatMap((step) => [
    `help.${step.key}.title`,
    `help.${step.key}.lead`,
    ...Array.from({ length: step.points }, (_, i) => `help.${step.key}.p${i + 1}`),
  ])

// Where storage is unavailable (a locked-down private window), remember for the
// session at least, so it is not in the way on every sheet that closes.
let seenThisSession = false

export function helpSeen() {
  if (seenThisSession) return true
  try {
    return globalThis.localStorage?.getItem(KEY) === '1'
  } catch {
    return false
  }
}

export function markHelpSeen() {
  seenThisSession = true
  try {
    globalThis.localStorage?.setItem(KEY, '1')
  } catch {
    // Nothing to do: the session flag above is the fallback.
  }
}

/**
 * Opens the help if this browser has never seen it. Marked as seen the moment
 * it opens, not when it is finished: closing it early is an answer too.
 * @returns {boolean} whether it opened
 */
export function openHelpOnce() {
  if (helpSeen()) return false
  markHelpSeen()
  openSheet('help')
  return true
}

/** The header's ? — always opens, from the first step. */
export const openHelp = () => openSheet('help')
